import { Controller } from "@hotwired/stimulus"
import { NativeBridge } from "../lib/native_bridge"

/**
 * NativeGpsController - GPS location handling for native apps
 *
 * Automatically uses native GPS when in Hotwire Native app,
 * falls back to browser Geolocation API otherwise.
 *
 * Usage:
 *   <div data-controller="native-gps"
 *        data-native-gps-latitude-target="latitude"
 *        data-native-gps-longitude-target="longitude"
 *        data-native-gps-accuracy-value="high">
 *     <input type="hidden" name="latitude" data-native-gps-target="latitude">
 *     <input type="hidden" name="longitude" data-native-gps-target="longitude">
 *     <button data-action="native-gps#requestLocation">Get Location</button>
 *   </div>
 */
export default class extends Controller {
  static targets = ["latitude", "longitude", "status", "button"]

  static values = {
    accuracy: { type: String, default: "high" },
    autoRequest: { type: Boolean, default: false }
  }

  connect() {
    this.isNative = NativeBridge.isNative()

    if (this.autoRequestValue) {
      this.requestLocation()
    }
  }

  /**
   * Request current location
   */
  async requestLocation() {
    this.setLoading(true)

    try {
      let location

      if (this.isNative) {
        // Use native GPS
        location = await NativeBridge.GPS.requestLocation({
          accuracy: this.accuracyValue
        })
      } else {
        // Fall back to browser Geolocation
        location = await this.getBrowserLocation()
      }

      this.updateLocation(location)
      this.dispatch("success", { detail: location })

    } catch (error) {
      console.error("[NativeGPS] Error:", error)
      this.setError(error.message)
      this.dispatch("error", { detail: { error: error.message } })
    } finally {
      this.setLoading(false)
    }
  }

  /**
   * Get location using browser Geolocation API
   */
  getBrowserLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"))
        return
      }

      const options = {
        enableHighAccuracy: this.accuracyValue === "high",
        timeout: 30000,
        maximumAge: 0
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          })
        },
        (error) => {
          let message
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = "位置權限被拒絕"
              break
            case error.POSITION_UNAVAILABLE:
              message = "無法取得位置資訊"
              break
            case error.TIMEOUT:
              message = "位置請求逾時"
              break
            default:
              message = "位置取得失敗"
          }
          reject(new Error(message))
        },
        options
      )
    })
  }

  /**
   * Update location targets with new coordinates
   */
  updateLocation(location) {
    if (this.hasLatitudeTarget) {
      this.latitudeTarget.value = location.latitude
    }

    if (this.hasLongitudeTarget) {
      this.longitudeTarget.value = location.longitude
    }

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = `已取得位置 (精確度: ${Math.round(location.accuracy)}m)`
      this.statusTarget.classList.remove("text-red-500")
      this.statusTarget.classList.add("text-green-600")
    }
  }

  /**
   * Set loading state
   */
  setLoading(loading) {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = loading
      this.buttonTarget.classList.toggle("opacity-50", loading)
    }

    if (this.hasStatusTarget && loading) {
      this.statusTarget.textContent = "正在取得位置..."
      this.statusTarget.classList.remove("text-red-500", "text-green-600")
    }
  }

  /**
   * Set error state
   */
  setError(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      this.statusTarget.classList.add("text-red-500")
      this.statusTarget.classList.remove("text-green-600")
    }
  }
}
